import { unstable_rethrow } from 'next/navigation';
import { FormEvent, useCallback } from 'react';

import { RecaptchaAction } from './RecaptchaAction';
import { DATADOG_USER_AGENT_REGEX } from './constants';
import { useRecaptchaCallback } from './useRecaptchaCallback';

export interface FormWithRecaptchaConfig {
    action: RecaptchaAction;
    onSubmit: (formData: FormData, recaptchaToken?: string) => Promise<void> | void;
    onError?: () => void;
}

/**
 * Hook that combines form submission with recaptcha validation.
 * Handles the recaptcha flow and calls the provided onSubmit callback with the token.
 */
export function useFormWithRecaptcha({
    action,
    onSubmit,
    onError,
}: FormWithRecaptchaConfig): (event: FormEvent<HTMLFormElement>) => void {
    const recaptchaCallback = useRecaptchaCallback(action);

    return useCallback(
        (event: FormEvent<HTMLFormElement>): void => {
            event.preventDefault();
            const formData = new FormData(event.currentTarget);

            if (DATADOG_USER_AGENT_REGEX.test(navigator.userAgent)) {
                void onSubmit(formData, '');
                return;
            }

            recaptchaCallback()
                .then((recaptchaToken) => {
                    return onSubmit(formData, recaptchaToken);
                })
                .catch((error) => {
                    // Let Next.js control-flow errors (e.g. redirect from a server action) bubble up to the router.
                    unstable_rethrow(error);
                    onError?.();
                });
        },
        [recaptchaCallback, onSubmit, onError],
    );
}
